// src/pages/Invoices.jsx - Invoice management component with @react-pdf/renderer integration
import { Link, NavLink, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import sortBy from "lodash/sortBy";
import { pdf } from "@react-pdf/renderer";
import QuotPrintPDF from "../components/invoices/QuotPrintPDF";

// Icon imports for UI elements
import IconTrashLines from "../components/icons/IconTrashLines";
import IconPlus from "../components/icons/IconPlus";
import IconEdit from "../components/icons/IconEdit";
import IconEye from "../components/icons/IconEye";
import IconUser from "../components/icons/IconUser";
import IconPrinter from "../components/icons/IconPrinter";
import IconDownload from "../components/icons/IconDownload";

// Service imports for API calls
import { deleteInvoice, getAllInvoice } from "../services/invoiceService";
import formatPrice from "../utils/formatPrice";

/**
 * Invoices Component - Displays invoice data in a clean, responsive table
 *
 * Features:
 * - Search functionality across multiple fields
 * - Sorting by any column
 * - Status filtering (Not Paid yet, Paid, Quote, Draft, Instalment)
 * - Pagination with configurable page sizes
 * - Bulk delete operations
 * - PDF generation and download using @react-pdf/renderer
 * - Loading and empty states
 * - Responsive design
 * - Consistent UI with CustomerTable
 */
const Invoices = () => {
  // URL search parameters for status filtering
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status"); // Get status from URL

  // State management for invoice data and table functionality
  const [items, setItems] = useState([]); // All invoice items from API
  const [filteredItems, setFilteredItems] = useState([]); // Items after search filter
  const [loading, setLoading] = useState(true); // Loading state for better UX
  const [selectedInvoices, setSelectedInvoices] = useState([]); // Selected rows for bulk operations
  const [generatingPdf, setGeneratingPdf] = useState(null); // Track which invoice is generating PDF

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

  // Search and sorting state
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "invoice",
    direction: "asc",
  });

  // Calculate pagination values
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredItems.slice(startIndex, endIndex);

  // Load all invoices from API on component mount
  useEffect(() => {
    const loadInvoices = async () => {
      try {
        setLoading(true);
        const response = await getAllInvoice();

        // DEBUG: Log all invoices with their customer and job IDs
        response.forEach((inv, index) => {
        });

        // Sort initially by invoice number
        const sortedData = sortBy(response, "invoice");
        setItems(sortedData);
        setFilteredItems(sortedData);
      } catch (error) {
        console.error("Error loading invoices:", error);
        alert("Error loading invoices. Please refresh the page.");
      } finally {
        setLoading(false);
      }
    };

    loadInvoices();
  }, []);

  // Handle search filtering and status filtering across multiple fields
  useEffect(() => {
    let filtered = [...items];

    // Apply status filter first if present
    if (statusFilter) {
      filtered = filtered.filter((item) => {
        const itemStatus = item.status?.tooltip || "";
        return itemStatus === statusFilter;
      });
    }

    // Apply search filter if present
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((item) => {
        return (
          String(item.invoice || "")
            .toLowerCase()
            .includes(searchLower) ||
          String(item.name || "")
            .toLowerCase()
            .includes(searchLower) ||
          String(item.email || "")
            .toLowerCase()
            .includes(searchLower) ||
          String(item.date || "")
            .toLowerCase()
            .includes(searchLower) ||
          String(item.amount || "")
            .toLowerCase()
            .includes(searchLower) ||
          String(item.status?.tooltip || "")
            .toLowerCase()
            .includes(searchLower)
        );
      });
    }

    setFilteredItems(filtered);
    setCurrentPage(0); // Reset to first page when filtering
  }, [searchTerm, items, statusFilter]);

  // Handle sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key, direction });

    const sortedItems = [...filteredItems].sort((a, b) => {
      const aValue = a[key] || "";
      const bValue = b[key] || "";

      // Handle different data types
      if (key === "amount") {
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      if (key === "date") {
        const aDate = new Date(aValue);
        const bDate = new Date(bValue);
        return direction === "asc" ? aDate - bDate : bDate - aDate;
      }

      // String comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (direction === "asc") {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });

    setFilteredItems(sortedItems);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setItemsPerPage(newSize);
    setCurrentPage(0); // Reset to first page
  };

  // Handle invoice selection for bulk operations
  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices((prev) => {
      if (prev.includes(invoiceId)) {
        return prev.filter((id) => id !== invoiceId);
      } else {
        return [...prev, invoiceId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === currentItems.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(currentItems.map((item) => item.id));
    }
  };

  // Enhanced delete function with better error handling and UX
  const handleDelete = async (invoiceId = null) => {
    const idsToDelete = invoiceId ? [invoiceId] : selectedInvoices;

    if (idsToDelete.length === 0) return;

    const confirmMessage =
      idsToDelete.length === 1
        ? "Are you sure you want to delete this invoice?"
        : `Are you sure you want to delete ${idsToDelete.length} selected invoices?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      setLoading(true);

      // Delete all selected invoices
      await Promise.all(idsToDelete.map(deleteInvoice));

      // Update state to remove deleted items
      const updatedItems = items.filter(
        (item) => !idsToDelete.includes(item.id)
      );
      setItems(updatedItems);
      setFilteredItems(
        updatedItems.filter((item) => {
          if (!searchTerm.trim()) return true;
          const searchLower = searchTerm.toLowerCase();
          return (
            String(item.invoice || "")
              .toLowerCase()
              .includes(searchLower) ||
            String(item.name || "")
              .toLowerCase()
              .includes(searchLower) ||
            String(item.email || "")
              .toLowerCase()
              .includes(searchLower) ||
            String(item.date || "")
              .toLowerCase()
              .includes(searchLower) ||
            String(item.amount || "")
              .toLowerCase()
              .includes(searchLower) ||
            String(item.status?.tooltip || "")
              .toLowerCase()
              .includes(searchLower)
          );
        })
      );

      setSelectedInvoices([]);

      const successMessage =
        idsToDelete.length === 1
          ? "Invoice deleted successfully"
          : `${idsToDelete.length} invoices deleted successfully`;

      alert(successMessage);
    } catch (error) {
      console.error("Error deleting invoice(s):", error);
      alert("Error deleting invoice(s). Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF generation and download
  const handleDownloadPDF = async (invoice) => {
    try {
      setGeneratingPdf(invoice.id);

      // Generate PDF blob
      const blob = await pdf(<QuotPrintPDF quote={invoice} />).toBlob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoice.invoice}.pdf`;
      link.click();

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setGeneratingPdf(null);
    }
  };

  // Handle print (opens PDF in new tab)
  const handlePrintPDF = async (invoice) => {
    try {
      setGeneratingPdf(invoice.id);

      // Generate PDF blob
      const blob = await pdf(<QuotPrintPDF quote={invoice} />).toBlob();

      // Create URL and open in new tab for printing
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");

      // Optional: Automatically trigger print dialog after a short delay
      if (printWindow) {
        setTimeout(() => {
          printWindow.print();
        }, 1000);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setGeneratingPdf(null);
    }
  };

  // Calculate total paid amount for each invoice
  const calculateTotalPaid = (payments) => {
    if (!payments || !Array.isArray(payments)) return 0;
    return payments.reduce(
      (total, payment) => total + (payment.amount || 0),
      0
    );
  };

  // Render status badge with appropriate styling
  const renderStatusBadge = (status) => {
    if (!status) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">
          Unknown
        </span>
      );
    }

    const colorClasses = {
      success: "bg-green-100 text-green-800",
      warning: "bg-yellow-100 text-yellow-800",
      danger: "bg-red-100 text-red-800",
      default: "bg-gray-100 text-gray-800",
    };

    const colorClass = colorClasses[status.color] || colorClasses.default;

    return (
      <span
        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${colorClass}`}
      >
        {status.tooltip || "Unknown"}
      </span>
    );
  };

  // Render sort icon for table headers using simple text arrows
  const renderSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400 ml-1 text-xs">↕</span>;
    }
    return sortConfig.direction === "asc" ? (
      <span className="text-gray-600 ml-1 text-xs">↑</span>
    ) : (
      <span className="text-gray-600 ml-1 text-xs">↓</span>
    );
  };

  // Show loading state with animated spinner
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-8 flex justify-center">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header section with actions and search */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        {/* Status filter indicator */}
        {statusFilter && (
          <div className="mb-4 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm text-blue-600 font-medium">
                  Filtered by status: <strong>{statusFilter}</strong>
                </span>
                <span className="ml-2 text-xs text-blue-500">
                  ({filteredItems.length} invoice
                  {filteredItems.length !== 1 ? "s" : ""})
                </span>
              </div>
              <Link
                to="/invoices"
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Clear filter
              </Link>
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Action buttons */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-colors ${
                selectedInvoices.length === 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              }`}
              onClick={() => handleDelete()}
              disabled={selectedInvoices.length === 0}
              title={
                selectedInvoices.length === 0
                  ? "Select invoices to delete"
                  : `Delete ${selectedInvoices.length} selected invoice(s)`
              }
            >
              <IconTrashLines className="w-4 h-4 mr-2" />
              Delete{" "}
              {selectedInvoices.length > 0 && `(${selectedInvoices.length})`}
            </button>

            <Link
              to="/invoices/add"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
            >
              <IconPlus className="w-4 h-4 mr-2" />
              Add New Invoice
            </Link>
          </div>

          {/* Search input and page size selector */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700">
                Show:
              </label>
              <select
                id="pageSize"
                value={itemsPerPage}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            <input
              type="text"
              placeholder="Search invoices, names, emails..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-[250px]"
            />
          </div>
        </div>
      </div>

      {/* Invoice table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Show empty state when no invoices are found */}
        {filteredItems.length === 0 ? (
          <div className="p-8 text-center">
            <div className="flex flex-col items-center">
              <IconUser className="w-16 h-16 text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {statusFilter
                  ? `No ${statusFilter} Invoices Found`
                  : searchTerm
                  ? "No Invoices Found"
                  : "No Invoices Yet"}
              </h3>
              <p className="mt-2 text-gray-500">
                {statusFilter && !searchTerm
                  ? `There are no invoices with the status "${statusFilter}". Try selecting a different status filter.`
                  : statusFilter && searchTerm
                  ? `No invoices found matching your search and status filter. Try adjusting your filters.`
                  : searchTerm
                  ? "Try adjusting your search terms or clear the search to see all invoices."
                  : "Get started by creating your first invoice."}
              </p>
              {statusFilter && (
                <Link
                  to="/invoices"
                  className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  Clear Status Filter
                </Link>
              )}
              {!searchTerm && !statusFilter && (
                <Link
                  to="/invoices/add"
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                >
                  <IconPlus className="w-4 h-4 mr-2" />
                  Add Invoice
                </Link>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Responsive table with horizontal scroll on small screens */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* Select all checkbox */}
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={
                          selectedInvoices.length === currentItems.length &&
                          currentItems.length > 0
                        }
                        onChange={handleSelectAll}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                    </th>

                    {/* Sortable table headers */}
                    {[
                      { key: "invoice", label: "Invoice #" },
                      { key: "name", label: "Customer" },
                      { key: "email", label: "Email" },
                      { key: "date", label: "Date" },
                      { key: "amount", label: "Amount" },
                      { key: "payments", label: "Total Paid" },
                      { key: "status", label: "Status" },
                    ].map((column) => (
                      <th
                        key={column.key}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={() => handleSort(column.key)}
                      >
                        <div className="flex items-center gap-1">
                          {column.label}
                          {renderSortIcon(column.key)}
                        </div>
                      </th>
                    ))}

                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {/* Map through current page invoices to create table rows */}
                  {currentItems.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className={`hover:bg-gray-50 transition-colors ${
                        selectedInvoices.includes(invoice.id)
                          ? "bg-indigo-50"
                          : ""
                      }`}
                    >
                      {/* Selection checkbox */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedInvoices.includes(invoice.id)}
                          onChange={() => handleSelectInvoice(invoice.id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                      </td>

                      {/* Invoice number cell */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <NavLink
                          to={`/invoices/${invoice.invoice}`}
                          className="text-indigo-600 hover:text-indigo-900 font-medium transition-colors"
                        >
                          #{invoice.invoice || "—"}
                        </NavLink>
                      </td>

                      {/* Customer name cell with avatar */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center">
                            <IconUser className="h-4 w-4 text-indigo-600" />
                          </div>
                          <div className="ml-3">
                            <div
                              className="text-sm font-medium text-gray-900 max-w-[150px] truncate"
                              title={invoice.name}
                            >
                              {invoice.name || "—"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Email cell */}
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                        <div
                          className="truncate max-w-[200px]"
                          title={invoice.email}
                        >
                          {invoice.email || "—"}
                        </div>
                      </td>

                      {/* Date cell */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {invoice.date
                          ? new Date(invoice.date).toLocaleDateString()
                          : "—"}
                      </td>

                      {/* Amount cell */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatPrice(Number(invoice.amount) || 0)}
                      </td>

                      {/* Total paid cell */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {formatPrice(calculateTotalPaid(invoice.payments))}
                      </td>

                      {/* Status cell */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {renderStatusBadge(invoice.status)}
                      </td>

                      {/* Actions cell */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <NavLink
                            to={`/invoices/${invoice.invoice}`}
                            className="text-indigo-600 hover:text-indigo-900 transition-colors p-1 rounded-full hover:bg-indigo-50"
                            title="Edit invoice"
                          >
                            <IconEdit className="h-4 w-4" />
                          </NavLink>

                          <NavLink
                            to={`/invoices/${invoice.invoice}`}
                            className="text-green-600 hover:text-green-900 transition-colors p-1 rounded-full hover:bg-green-50"
                            title="View invoice"
                          >
                            <IconEye className="h-4 w-4" />
                          </NavLink>

                          <button
                            type="button"
                            className="text-blue-600 hover:text-blue-900 transition-colors p-1 rounded-full hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handlePrintPDF(invoice)}
                            title="Print invoice"
                            disabled={generatingPdf === invoice.id}
                          >
                            {generatingPdf === invoice.id ? (
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <IconPrinter className="h-4 w-4" />
                            )}
                          </button>

                          <button
                            type="button"
                            className="text-purple-600 hover:text-purple-900 transition-colors p-1 rounded-full hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handleDownloadPDF(invoice)}
                            title="Download PDF"
                            disabled={generatingPdf === invoice.id}
                          >
                            {generatingPdf === invoice.id ? (
                              <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <IconDownload className="h-4 w-4" />
                            )}
                          </button>

                          <button
                            type="button"
                            className="text-red-600 hover:text-red-900 transition-colors p-1 rounded-full hover:bg-red-50"
                            onClick={() => handleDelete(invoice.id)}
                            title="Delete invoice"
                          >
                            <IconTrashLines className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="flex-1 flex justify-between items-center">
                  <div className="text-sm text-gray-700">
                    Showing {startIndex + 1} to{" "}
                    {Math.min(endIndex, filteredItems.length)} of{" "}
                    {filteredItems.length} entries
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 0}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md transition-colors ${
                        currentPage === 0
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Previous
                    </button>

                    <span className="text-sm text-gray-700">
                      Page {currentPage + 1} of {totalPages}
                    </span>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage >= totalPages - 1}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md transition-colors ${
                        currentPage >= totalPages - 1
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Invoices;
